<template>
    <section :class="`cta-tile cta-tile--${this.type}${this.name ? ` cta-tile--${this.name}` : ''} ${this.breakoutExtended ? 'cta-tile--breakout-mid' : this.breakout ? 'cta-tile--breakout' : 'cta-tile--inline'}`">
        <div :class="`cta-tile__inner${this.hasSecondaryButton ? ' cta-tile__inner--2button' : ''}`">
            <h3 v-if="title"
                class="cta-tile__title --not-breakout">
                {{ title }}
            </h3>
            <div v-if="renderFullImage && imageUrl"
                 :class="`cta-tile__image cta-tile__image--${this.imageName ? this.imageName : this.name} --not-breakout`">
                <img :src="imageUrl" :alt="titleOrName">
            </div>
            <div v-else-if="renderFullImage"
                 :class="`cta-tile__image cta-tile__image--${this.imageName ? this.imageName : this.name} --not-breakout`" />
            <div v-else-if="renderSmallImage"
                 :class="`cta-tile__small-image cta-tile__small-image--${this.imageName ? this.imageName : this.name} --not-breakout`">
                <img :src="imageUrl" :alt="titleOrName">
            </div>
            <span v-if="slotPassed"
                  class="cta-tile__description --not-breakout"><slot /></span>
            <div class="cta-tile__buttons">
                <hh-btn :def="`tile--${this.name}-btn`"
                        :class="`cta-tile__button cta-tile__button--${this.name}`"
                        :disabled="disabled"
                        :icon-class="iconClass"
                        :icon-location="iconLocation"
                        :text="buttonText"
                        :type="type"
                        :track-operation-state="true"
                        :operation-in-progress="buttonOperationInProgress"
                        @click="$emit('click')" />
                <hh-btn v-if="hasSecondaryButton"
                        :def="`tile--${this.name}-secondary-btn`"
                        :class="`cta-tile__button-secondary cta-tile__button-secondary--${this.name}`"
                        :disabled="secondaryButtonDisabled"
                        :icon-class="secondaryButtonIconClass"
                        :icon-location="secondaryButtonIconLocation"
                        :text="secondaryButtonText"
                        :type="secondaryButtonType"
                        :track-operation-state="true"
                        :operation-in-progress="secondaryButtonOperationInProgress"
                        @click="$emit('secondary-click')" />
            </div>
        </div>
    </section>
</template>

<script>

import Cta from './cta'

export default {

    props: {
        buttonOperationInProgress: {
            type: Boolean,
            default: null,
        },
        iconClass: {
            type: String,
            default: null,
        },
        iconLocation: {
            type: String,
            default: null,
        },
        imageName: {
            type: String,
            default: null,
        },
        imageUrl: {
            type: String,
            default: null,
        },
        secondaryButtonDisabled: {
            type: Boolean,
            default: false,
        },
        secondaryButtonOperationInProgress: {
            type: Boolean,
            default: null,
        },
        secondaryButtonText: {
            type: String,
            default: null,
        },
        secondaryButtonType: {
            type: String,
            default: 'cancel',
        },
        secondaryButtonIconClass: {
            type: String,
            default: null,
        },
        secondaryButtonIconLocation: {
            type: String,
            default: null,
        },
        showSmallImage: {
            type: Boolean,
            default: false,
        },
        type: {
            type: String,
            default: 'cta',
        },
    },

    mixins: [
        Cta,
    ],

    computed: {

        hasSecondaryButton() {
            return this.secondaryButtonText && this.secondaryButtonText.length
        },

        renderFullImage() {
            return (this.imageName || this.imageUrl) && !this.showSmallImage
        },

        renderSmallImage() {
            return (this.imageName || this.imageUrl) && this.showSmallImage
        },

        slotPassed() {
            return this.$slots.default && this.$slots.default[0].text.length
        },
    },
}

</script>

<style lang="scss">

    // Ensure colours and other visual style elements are added to theme files, not placed in here.
    // src\scss\schemes\components\my-home\_cta-tile.scss

    .cta-tile {
        font-size: 0.8rem;
        position: relative;

        @include modifier('grid') {
            .cta-tile__inner--2button .cta-tile__buttons {
                @include bp-min-small {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1rem;
                }

                @include bp-min-mid {
                    max-width: rem(600px);
                }
            }
        }

        @include element('inner') {
            position: relative;
            margin: 0 auto;
            padding-bottom: $cta-tile-breakout-single-height;

            @include modifier('2button') {
                padding-bottom: $cta-tile-breakout-double-height;
            }
        }

        @include element('title') {
            font-size: 1.1rem;
            margin-bottom: rem(5px);
        }

        @include elements('image' 'small-image') {
            background: transparent;
        }

        @include element('image') {
            width: 100%;
            height: 120px;
            background-position: center;
            background-repeat: no-repeat;
            background-size: cover;

            @include modifier('review') {
                background-image: url('~@/assets/review.jpg');
            }

            @include modifier('survey') {
                background-image: url('~@/assets/survey.jpg');
            }

            @include modifier('build') {
                background-image: url('~@/assets/improve.jpg');
            }
        }

        @include element('small-image') {
            width: 100%;
            max-height: 40px;
            margin-bottom: rem(5px);

            img {
                display: block;
                margin: 0 auto;
                max-height: 40px;
            }
        }

        @include element('description') {
            display: block;
            margin: rem(5px) 0;
        }

        @include elements('button' 'button-secondary') {
            width: 100%;
        }

        @include element('button-secondary') {
            font-size: 0.8rem;
        }

        @include modifier('branded') {

            @include bp-min-small {

                .cta-tile__inner {
                    max-width: 400px;
                }
            }
        }

        @include modifier('breakout') {
            @include breakout-buttons;

            @include bp-min-small {
                @include clear-breakout-buttons;
            }
        }

        @include modifier('breakout-mid') {
            @include breakout-buttons;

            @include bp-min-small {
                @include clear-breakout-buttons;
                position: static;

                .cta-tile {

                    @include element('inner') {
                        max-width: none;
                        padding-bottom: 0;
                        margin-top: 1rem;
                    }

                    @include element('buttons') {
                        display: flex;
                        flex-flow: row-reverse;
                        justify-content: space-between;
                    }

                    @include element('button') {
                        flex: 2 1 65%;
                    }

                    @include element('button-secondary') {
                        flex: 1 1 35%;
                        margin-right: 1rem;
                    }
                }
            }

            @include bp-min-mid {

                .cta-tile {
                    @include element('inner') {
                        padding-bottom: $cta-tile-breakout-double-height;
                    }

                    @include element('buttons') {
                        display: block;
                    }

                    @include element('button-secondary') {
                        margin-right: 0
                    }
                }

                .--not-breakout {
                    display: block;
                }
            }
        }

        .button {
            margin-top: 10px;
        }
    }
</style>
